/**
 * Wrappers tipados para /api/suscripcion/*, /api/planes y /api/confirmacion/{ref256}.
 * §10 SRS — flujo de pago en 5 pasos.
 */

import { apiClient } from './apiClient'

export type BillingType = 'monthly' | 'yearly'
export type SubscriptionStatus = 'active' | 'pending' | 'past_due' | 'canceled'
export type ChangeType = 'new' | 'upgrade' | 'downgrade' | 'renewal'

export type Plan = {
  id: number
  name: string
  description: string | null
  monthly_price: number
  yearly_price: number
  features: string[]
  max_reports_per_month: number
  max_ai_enhancements_per_month: number
  max_clients: number
  ai_enhancement_enabled: boolean
  active: boolean
  visible: boolean
  recommended: boolean
  created_at: string
}

export type ActiveSubscription = {
  id: number
  status: SubscriptionStatus
  billing_type: BillingType
  start_date: string
  current_period_end: string
  plan: {
    id: number
    name: string
    description: string | null
    monthly_price: number
    yearly_price: number
    max_reports_per_month: number
    max_ai_enhancements_per_month: number
    max_clients: number
    ai_enhancement_enabled: boolean
  }
}

export type PendingPayment = {
  reference: string
  callback_ref_256bit: string
  amount: number
  created_at: string
}

export type SuscripcionState = {
  subscription: ActiveSubscription | null
  next_plan: Plan | null
  pending_payment: PendingPayment | null
}

export type CheckoutResponse =
  | {
      change_type: 'downgrade'
      message: string
      effective_at: string
      new_plan: Plan
    }
  | {
      change_type: 'new' | 'upgrade' | 'renewal'
      reference: string
      callback_ref_256bit: string
      amount: number
      url_callback: string
      payment_url: string
      new_plan: Plan
      billing_type: BillingType
    }

export type CallbackPaymentInfo = {
  payment: {
    reference: string
    status: 'pending' | 'paid' | 'failed'
    amount: number
    created_at: string
    paid_at: string | null
    bank_confirmation: string | null
  }
  plan: { id: number; name: string; description: string | null } | null
}

export const suscripcionClient = {
  planes: () => apiClient.get<{ items: Plan[] }>('/planes'),

  show: () => apiClient.get<SuscripcionState>('/suscripcion'),

  checkout: (plan_id: number, billing_type: BillingType) =>
    apiClient.post<CheckoutResponse>('/suscripcion/checkout', { plan_id, billing_type }),

  cancelDowngrade: () =>
    apiClient.post<{ canceled: boolean }>('/suscripcion/cancel-downgrade'),

  /** Endpoint público (sin auth): consulta el estado del pago tras el callback del banco. */
  showCallback: (ref256: string) =>
    apiClient.get<CallbackPaymentInfo>(`/confirmacion/${ref256}`, { skipAuth: true }),

  /** Endpoint público (sin auth): activa el pago con el id de confirmación del banco (?id=XXX). */
  confirmCallback: (ref256: string, bankConfirmation: string) =>
    apiClient.post<unknown>(`/confirmacion/${ref256}`, { bank_confirmation: bankConfirmation }, { skipAuth: true }),
}

// ─── Helpers de presentación ───────────────────────────────────────────────

export function formatMxn(amount: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 2,
  }).format(amount)
}

export function describeChangeType(t: ChangeType): string {
  switch (t) {
    case 'new':
      return 'Nueva contratación'
    case 'upgrade':
      return 'Upgrade (cobro prorrateado)'
    case 'downgrade':
      return 'Downgrade (programado al próximo ciclo)'
    case 'renewal':
      return 'Renovación'
  }
}

export const RENEWAL_WARNING_DAYS = 5

export function daysUntil(dateStr: string): number {
  const datePart = dateStr.split('T')[0].split(' ')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  const end = new Date(year, month - 1, day)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((end.getTime() - today.getTime()) / 86400000)
}
